import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import {} from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  faPlus, faTrash, faFolder, faFolderOpen, faQuestionCircle, faSearch, faTimes, faUpload, faFile, faCheck, faEye, faTag, faUnlink, faDownload, faRocket, faHistory
} from '@fortawesome/free-solid-svg-icons';
import { HeaderActionsComponent, HeaderActionsConfig } from '../../components/header-actions/header-actions.component';
import { SearchFilters } from '../../components/search/search.component';
import { ItemListComponent, ListItem, ListConfig } from '../../components/item-list/item-list.component';
import { ArchivesService, Archive } from '../../services/archives.service';
import { AlertService } from '../../components/alert-service/alert-service';
import { DateFormatterService } from '../../services/date-formatter.service';
import { TagManagerComponent, TaggableItem } from '../../components/tag-manager/tag-manager.component';
import { ModalUploadArchivesComponent } from '../../components/modal-upload-archives/modal-upload-archives.component';
import { ViewerComponent } from '../../components/viewer/viewer.component';
import { ModalDeleteConfirmComponent } from '../../components/modal-delete-confirm/modal-delete-confirm.component';
import { PreviewCacheService, PreviewMetadata } from '../../services/preview-cache.service';
import { PaginationComponent } from '../../components/pagination/pagination.component';
import { ModalDeployKnowledgeBaseComponent, DeployableArchive } from '../../components/modal-deploy-knowledge-base/modal-deploy-knowledge-base.component';
import { ModalKnowledgeBaseHistoryComponent } from '../../components/modal-knowledge-base-history/modal-knowledge-base-history.component';
import { CodeViewerComponent } from '../../components/code-viewer/code-viewer.component';

@Component({
  selector: 'app-archives',
  standalone: true,
  imports: [
    RouterModule,
    FontAwesomeModule,
    FormsModule,
    TranslateModule,
    HeaderActionsComponent,
    ItemListComponent,
    TagManagerComponent,
    ModalUploadArchivesComponent,
    ViewerComponent,
    ModalDeleteConfirmComponent,
    PaginationComponent,
    ModalDeployKnowledgeBaseComponent,
    ModalKnowledgeBaseHistoryComponent,
    CodeViewerComponent
],
  templateUrl: './archives.component.html',
  styleUrls: ['./archives.component.scss']
})
export class ArchivesComponent implements OnInit {
  isAdmin = sessionStorage.getItem('role') === 'ADMIN';
  archives: Archive[] = [];
  filteredArchives: Archive[] = [];
  loading = false;
  showUploadModal = false;
  uploadLoading = false;
  uploadProgress: { current: number; total: number } | null = null;
  showDeleteModal = false;
  archiveToDelete: ListItem | null = null;

  showDeployKbModal = false;
  showKbHistoryModal = false;
  selectedArchiveForKb: DeployableArchive | null = null;

  selectedFiles: File[] = [];

  showPreviewModal = false;
  previewLoading = false;
  previewBatchLoading = false;
  previewPages: string[] = [];
  currentPreviewArchive: Archive | null = null;
  currentArchiveId: string | number | null = null;
  previewMetadata: PreviewMetadata | null = null;
  jsonPreviewContent = '';
  isJsonPreview = false;

  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  private readonly PREVIEW_SUPPORTED_EXTENSIONS = [
    'pdf', 'png', 'jpg', 'jpeg',
    'doc', 'docx', 'odt',
    'xls', 'xlsx', 'ods', 'csv',
    'ppt', 'pptx', 'odp',
    'json'
  ];

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('folderInput') folderInput!: ElementRef<HTMLInputElement>;
  @ViewChild(TagManagerComponent) tagManager!: TagManagerComponent;

  headerConfig: HeaderActionsConfig = {
    addButton: {
      type: 'add',
      text: 'BUTTON.UPLOAD',
      icon: faPlus,
      visible: true,
      disabled: false
    },
    helpButton: {
      type: 'help',
      icon: faQuestionCircle,
      visible: false,
      disabled: true
    },
    searchConfig: {
      showTextSearch: true,
      showTagFilter: true,
      textPlaceholder: 'SEARCH.PLACEHOLDER.ARCHIVES',
      tagPlaceholder: 'SEARCH.PLACEHOLDER.TAGS'
    }
  };

  listConfig: ListConfig = {
    itemIcon: faFolder,
    itemIconClass: 'artifact-icon',
    showMetadata: true,
    showType: false,
    emptyMessage: 'MESSAGE.NO.ARCHIVES',
    emptyMessageFiltered: 'MESSAGE.NO.ARCHIVES.FILTERED',
    actions: [
      {
        id: 'manage-tags',
        label: 'ARCHIVES.ACTIONS.MANAGE_TAGS',
        icon: faTag,
        cssClass: 'link-tag-item'
      },
      {
        id: 'download',
        label: 'BUTTON.DOWNLOAD',
        icon: faDownload,
        cssClass: 'download-item'
      },
      {
        id: 'delete',
        label: 'BUTTON.DELETE',
        icon: faTrash,
        cssClass: 'delete-item'
      }
    ]
  };

  currentFilters: SearchFilters = {
    searchTerm: '',
    selectedTag: ''
  };

  faPlus = faPlus;
  faTrash = faTrash;
  faFolder = faFolder;
  faQuestionCircle = faQuestionCircle;
  faSearch = faSearch;
  faTimes = faTimes;
  faFolderOpen = faFolderOpen;
  faUpload = faUpload;
  faFile = faFile;
  faCheck = faCheck;
  faEye = faEye;
  faTag = faTag;
  faUnlink = faUnlink;
  faDownload = faDownload;

  constructor(
    private service: ArchivesService,
    private translate: TranslateService,
    private alert: AlertService,
    private dateFormatter: DateFormatterService,
    private previewCache: PreviewCacheService
  ) {}

  ngOnInit(): void {
    if (this.isAdmin) {
      const deleteIndex = this.listConfig.actions.findIndex(a => a.id === 'delete');
      const kbActions = [
        { id: 'deploy-kb', label: 'ARCHIVES.DEPLOY_KB', icon: faRocket, cssClass: 'deploy-kb-item' },
        { id: 'kb-history', label: 'ARCHIVES.KB_HISTORY', icon: faHistory, cssClass: 'kb-history-item' },
      ];
      const at = deleteIndex >= 0 ? deleteIndex : this.listConfig.actions.length;
      this.listConfig.actions.splice(at, 0, ...kbActions);
    }
    this.loadArchives();
  }

  onAddArchive(): void {
    this.showUploadModal = true;
    this.selectedFiles = [];
  }

  onUploadSuccess(): void {
    this.loadArchives();
  }

  closeUploadModal(): void {
    this.showUploadModal = false;
    this.uploadLoading = false;
    this.uploadProgress = null;
    this.selectedFiles = [];
    if (this.fileInput) this.fileInput.nativeElement.value = '';
    if (this.folderInput) this.folderInput.nativeElement.value = '';
  }

  triggerFileInput(): void {
    this.fileInput?.nativeElement?.click();
  }

  triggerFolderInput(): void {
    this.folderInput?.nativeElement?.click();
  }

  onFileSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.selectedFiles = [...this.selectedFiles, ...Array.from(input.files)];
    }
  }

  onFolderSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.selectedFiles = [...this.selectedFiles, ...Array.from(input.files)];
    }
  }

  removeFile(idx: number): void {
    this.selectedFiles.splice(idx, 1);
  }

  clearSelectedFiles(): void {
    this.selectedFiles = [];
  }

  confirmUpload(): void {
    if (this.selectedFiles.length === 0) return;
    this.uploadLoading = true;
    this.uploadProgress = { current: 0, total: this.selectedFiles.length };

    this.service.upload(this.selectedFiles).subscribe({
      next: () => {
        this.alert.showSuccess(this.translate.instant('ARCHIVES.UPLOAD.SUCCESS'));
        this.closeUploadModal();
        this.loadArchives();
      },
      error: (err) => {
        this.uploadLoading = false;
        this.alert.showError(err);
      }
    });
  }

  onFiltersChanged(filters: SearchFilters) {
    this.currentFilters = filters;
    this.applyFilters();
  }

  onSearchCleared() {
    this.currentFilters.searchTerm = '';
    this.applyFilters();
  }

  onTagFilterCleared() {
    this.currentFilters.selectedTag = '';
    this.applyFilters();
  }

  onAllFiltersCleared() {
    this.currentFilters = { searchTerm: '', selectedTag: '' };
    this.applyFilters();
  }

  get archivesAsListItems(): ListItem[] {
    return this.filteredArchives.map(a => ({
      id: a.id,
      name: a.name,
      created_date: a.created_at ? this.dateFormatter.formatDate(a.created_at) : '',
      created_time: a.created_at ? this.dateFormatter.formatTime(a.created_at) : '',
      tag: a.tag || null,
      tags: a.tags || []
    }));
  }

  onItemClick(item: ListItem): void {
    const archive = this.archives.find(a => a.id === item.id);
    if (!archive) return;

    if (this.supportsPreview(archive.name)) {
      this.openPreviewModal(item);
    } else {
      this.downloadArchiveFile(item);
    }
  }

  onActionClick(event: { action: string; item: ListItem; event: Event }): void {
    if (event.action === 'manage-tags') {
      this.handleManageTags(event.item);
    }
    if (event.action === 'download') {
      this.downloadArchiveFile(event.item);
    }
    if (event.action === 'deploy-kb') {
      this.openDeployKbModal(event.item);
    }
    if (event.action === 'kb-history') {
      this.openKbHistoryModal(event.item);
    }
    if (event.action === 'delete') {
      this.deleteArchive(event.item.id, event.event);
    }
  }

  openDeployKbModal(item: ListItem): void {
    this.selectedArchiveForKb = { id: String(item.id), name: item.name };
    this.showDeployKbModal = true;
  }

  openKbHistoryModal(item: ListItem): void {
    this.selectedArchiveForKb = { id: String(item.id), name: item.name };
    this.showKbHistoryModal = true;
  }

  openPreviewModal(item: ListItem): void {
    const archive = this.archives.find(a => a.id === item.id);
    if (!archive) return;

    this.currentPreviewArchive = archive;
    this.showPreviewModal = true;
    this.previewLoading = true;
    this.previewPages = [];
    this.previewMetadata = null;
    this.currentArchiveId = item.id;

    this.isJsonPreview = this.isJsonFile(archive.name);
    if (this.isJsonPreview) {
      this.loadJsonPreview(item.id);
    } else {
      this.loadPreviewBatch(1);
    }
  }

  private loadJsonPreview(id: string | number): void {
    this.service.download(id).subscribe({
      next: async blob => {
        try {
          this.jsonPreviewContent = await blob.text();
          this.previewLoading = false;
        } catch (err) {
          this.previewLoading = false;
          this.alert.showError(err);
        }
      },
      error: err => {
        this.previewLoading = false;
        this.alert.showError(err);
      },
    });
  }

  private isJsonFile(filename: string): boolean {
    return filename.split('.').pop()?.toLowerCase() === 'json';
  }

  private loadPreviewBatch(page: number): void {
    if (!this.currentArchiveId) return;

    if (page > 1) {
      this.previewBatchLoading = true;
    }

    this.previewCache.getPreviewPages(this.currentArchiveId, page).subscribe({
      next: ({ pages, metadata }) => {
        if (page === 1) {
          this.previewPages = pages.map(p => p.base64Data);
        } else {
          this.previewPages = [...this.previewPages, ...pages.map(p => p.base64Data)];
        }
        this.previewMetadata = metadata;
        this.previewLoading = false;
        this.previewBatchLoading = false;
      },
      error: (err) => {
        this.previewLoading = false;
        this.previewBatchLoading = false;
        if (page === 1) {
          const archiveId = this.currentArchiveId;
          this.closePreviewModal();
          this.downloadArchiveFile({ id: archiveId } as ListItem);
        }
      }
    });
  }

  onPreviewPageChange(newIndex: number): void {
    if (!this.previewMetadata || !this.currentArchiveId) return;
    if (this.previewBatchLoading) return;

    const loadedPages = this.previewPages.length;
    const requestedPageNumber = newIndex + 1;

    if (requestedPageNumber > loadedPages) {
      const nextBatchPage = Math.ceil(requestedPageNumber / this.previewMetadata.pageSize);
      this.loadPreviewBatch(nextBatchPage);
    }
  }

  supportsPreview(filename: string): boolean {
    const extension = filename.split('.').pop()?.toLowerCase();
    return extension ? this.PREVIEW_SUPPORTED_EXTENSIONS.includes(extension) : false;
  }

  downloadArchiveFile(item: ListItem): void {
    const archive = this.archives.find(a => a.id === item.id);
    const filename = archive?.name || `archive-${item.id}`;

    this.service.download(item.id).subscribe({
      next: (blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      },
      error: (err) => {
        this.alert.showError(err);
      }
    });
  }

  deleteArchive(id: number | string, ev?: Event) {
    ev?.stopPropagation();
    if (!id) return;

    const archive = this.archives.find(a => a.id === id);
    if (!archive) return;

    this.archiveToDelete = {
      id: archive.id,
      name: archive.name,
      created_date: archive.created_at ? this.dateFormatter.formatDate(archive.created_at) : '',
      created_time: archive.created_at ? this.dateFormatter.formatTime(archive.created_at) : '',
      tag: archive.tag || null
    };
    this.showDeleteModal = true;
  }

  onDeleteConfirm() {
    if (this.archiveToDelete) {
      this.service.delete(this.archiveToDelete.id).subscribe({
        next: () => {
          this.alert.showSuccess(this.translate.instant('ARCHIVES.DELETE.SUCCESS'));
          
          this.previewCache.clearCache(this.archiveToDelete!.id);
          
          this.loadArchives();
          this.showDeleteModal = false;
          this.archiveToDelete = null;
        },
        error: (err) => {
          this.alert.showError(err);
          this.showDeleteModal = false;
          this.archiveToDelete = null;
        }
      });
    }
  }

  onDeleteClose() {
    this.showDeleteModal = false;
    this.archiveToDelete = null;
  }

  handleManageTags(item: ListItem): void {
    const taggableItem: TaggableItem = {
      id: String(item.id),
      name: item.name,
      tags: (item.tags as TaggableItem['tags']) || []
    };
    this.tagManager?.openLinkModal(taggableItem);
  }

  onTagChanged(): void {
    this.loadArchives();
  }

  loadArchives(): void {
    this.loading = true;
    const tagFilter = this.currentFilters.selectedTag?.trim() || undefined;
    const searchFilter = this.currentFilters.searchTerm?.trim() || undefined;

    this.service.list(tagFilter, searchFilter, this.currentPage, this.pageSize).subscribe({
      next: (response) => {
        this.archives = response.archives || [];
        this.totalItems = response.total;
        this.totalPages = response.total_pages;
        this.filteredArchives = [...this.archives];
        this.loading = false;
      },
      error: (err) => {
        this.alert.showError(err);
        this.loading = false;
        this.archives = [];
        this.filteredArchives = [];
        this.totalItems = 0;
        this.totalPages = 0;
      }
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadArchives();
  }

  private applyFilters(): void {
    this.currentPage = 1;
    this.loadArchives();
  }

  private applyLocalFilters(): void {
    this.filteredArchives = [...this.archives];
  }

  get hasActiveFilters(): boolean {
    return !!this.currentFilters.searchTerm || !!this.currentFilters.selectedTag;
  }

  closePreviewModal(): void {
    this.showPreviewModal = false;
    this.previewPages = [];
    this.currentPreviewArchive = null;
    this.currentArchiveId = null;
    this.previewLoading = false;
    this.previewMetadata = null;
    this.jsonPreviewContent = '';
    this.isJsonPreview = false;
  }
}
